import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAppContext, type DecryptedBlock } from "@/context/AppContext";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  getTablePayload,
  packFormRows,
  type SchemaField,
  type TablePayload,
  type TableView,
  type TableViewSort,
  type TableDefaultView,
} from "@/lib/tableBlockHelpers";
import { cn } from "@/lib/utils";
import { encrypt } from "@/utls/CryptoHelpers";

const SCHEMA_TYPE_OPTIONS = [
  "string",
  "number",
  "date",
  "checkbox",
  "select",
  "mediaList",
  "textarea",
  "markdown",
  "html",
] as const;

type TableEditTab = "general" | "schema" | "views";

const TABLE_EDIT_TABS: { id: TableEditTab; label: string }[] = [
  { id: "general", label: "General" },
  { id: "schema", label: "Schema fields" },
  { id: "views", label: "Views" },
];

type SchemaFieldDraft = {
  id: string;
  name: string;
  key: string;
  type: string;
  cellEditor: string;
  colWidth: string;
  optionsText: string;
  autofillCurrentDate: boolean;
  formSpan: 1 | 2;
};

type ViewConfigDraft = {
  name: string;
  columns: string[];
  editFields: string[];
  filtersJson: string;
  sortsJson: string;
  extractRegex: string;
};

type ViewDraft = ViewConfigDraft & {
  key: string;
};

type TableDraft = {
  name: string;
  isDefault: boolean;
  schema: SchemaFieldDraft[];
  defaultView: ViewConfigDraft;
  views: ViewDraft[];
};

function linesToArray(text: string): string[] | undefined {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : undefined;
}

function arrayToLines(values: string[] | undefined): string {
  return values?.join("\n") ?? "";
}

function nameToFieldKey(name: string): string {
  const words = name.trim().split(/[\s_-]+/).filter(Boolean);
  if (words.length === 0) return "";
  return words
    .map((word, index) => {
      const cleaned = word.replace(/[^a-zA-Z0-9]/g, "");
      if (!cleaned) return "";
      if (index === 0) {
        return cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
      }
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    })
    .join("");
}

function schemaFieldToDraft(
  field: SchemaField,
  index: number = 0,
): SchemaFieldDraft {
  return {
    id: `field-${field.key || index}-${Math.random().toString(36).slice(2, 9)}`,
    name: field.name,
    key: field.key,
    type: field.type,
    cellEditor: field.cellEditor ?? "",
    colWidth: field.colWidth !== undefined ? String(field.colWidth) : "",
    optionsText: arrayToLines(field.options),
    autofillCurrentDate: field.autofillCurrentDate === true,
    formSpan: field.formSpan === 1 ? 1 : 2,
  };
}

function viewConfigToDraft({
  name,
  view,
}: {
  name: string;
  view: TableDefaultView | TableView | undefined;
}): ViewConfigDraft {
  return {
    name: view && "name" in view && view.name ? view.name : name,
    columns: view?.columns ?? [],
    editFields: view?.editFields ?? [],
    filtersJson:
      view?.filters !== undefined ? JSON.stringify(view.filters, null, 2) : "",
    sortsJson: view?.sorts !== undefined ? JSON.stringify(view.sorts, null, 2) : "",
    extractRegex: view?.quickAdd?.extractRegex ?? "",
  };
}

function viewToDraft(view: TableView): ViewDraft {
  return {
    key: view.key,
    ...viewConfigToDraft({ name: view.name, view }),
  };
}

function payloadToDraft(payload: TablePayload): TableDraft {
  return {
    name: payload.name ?? "",
    isDefault: payload.isDefault === true,
    schema: payload.schema.map((f, i) => schemaFieldToDraft(f, i)),
    defaultView: viewConfigToDraft({ name: "Default", view: payload.defaultView }),
    views: (payload.views ?? []).map(viewToDraft),
  };
}

function parseJsonObject({
  raw,
  label,
}: {
  raw: string;
  label: string;
}): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: true, value: {} };
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ok: false, error: `${label} must be a JSON object.` };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Invalid JSON";
    return { ok: false, error: `${label}: ${msg}` };
  }
}

function draftFieldToSchema(field: SchemaFieldDraft): SchemaField | null {
  const name = field.name.trim();
  const key = field.key.trim();
  const type = field.type.trim();
  if (!name || !key || !type) return null;

  const result: SchemaField = { name, key, type };
  const cellEditor = field.cellEditor.trim();
  if (cellEditor) result.cellEditor = cellEditor;

  const colWidthRaw = field.colWidth.trim();
  if (colWidthRaw) {
    const colWidth = Number(colWidthRaw);
    if (!Number.isFinite(colWidth)) return null;
    result.colWidth = colWidth;
  }

  const options = linesToArray(field.optionsText);
  if (options) result.options = options;

  if (type === "date" && field.autofillCurrentDate) {
    result.autofillCurrentDate = true;
  }

  if (field.formSpan === 1) {
    result.formSpan = 1;
  }

  return result;
}

function buildViewConfigFromDraft({
  viewDraft,
  label,
}: {
  viewDraft: ViewConfigDraft;
  label: string;
}):
  | { ok: true; config: Record<string, unknown>; name: string }
  | { ok: false; error: string } {
  const name = viewDraft.name.trim();
  if (!name) {
    return { ok: false, error: `${label} must have a name.` };
  }

  const filtersParsed = parseJsonObject({
    raw: viewDraft.filtersJson,
    label: `${label} filters`,
  });
  if (!filtersParsed.ok) {
    return { ok: false, error: filtersParsed.error };
  }

  const sortsParsed = parseJsonObject({
    raw: viewDraft.sortsJson,
    label: `${label} sorts`,
  });
  if (!sortsParsed.ok) {
    return { ok: false, error: sortsParsed.error };
  }

  const config: Record<string, unknown> = { name };
  if (viewDraft.columns.length > 0) config.columns = viewDraft.columns;
  if (viewDraft.editFields.length > 0) config.editFields = viewDraft.editFields;
  if (Object.keys(filtersParsed.value).length > 0) {
    config.filters = filtersParsed.value;
  }
  if (Object.keys(sortsParsed.value).length > 0) {
    config.sorts = sortsParsed.value as Record<string, TableViewSort>;
  }
  const extractRegex = viewDraft.extractRegex.trim();
  if (extractRegex) {
    config.quickAdd = { extractRegex };
  }

  return { ok: true, config, name };
}

function buildPayloadFromDraft(draft: TableDraft):
  | { ok: true; data: Record<string, unknown> }
  | { ok: false; error: string } {
  const schema: SchemaField[] = [];
  const schemaKeys = new Set<string>();

  for (const fieldDraft of draft.schema) {
    const field = draftFieldToSchema(fieldDraft);
    if (!field) {
      return {
        ok: false,
        error: "Each schema field must have a name, key, and type.",
      };
    }
    if (schemaKeys.has(field.key)) {
      return { ok: false, error: `Duplicate schema key "${field.key}".` };
    }
    schemaKeys.add(field.key);
    schema.push(field);
  }

  const defaultViewBuilt = buildViewConfigFromDraft({
    viewDraft: draft.defaultView,
    label: "Default view",
  });
  if (!defaultViewBuilt.ok) {
    return { ok: false, error: defaultViewBuilt.error };
  }

  const views: Record<string, unknown>[] = [];
  const viewKeys = new Set<string>();

  for (const viewDraft of draft.views) {
    const key = viewDraft.key.trim();
    if (!key) {
      return { ok: false, error: "Each view must have a key." };
    }
    if (viewKeys.has(key)) {
      return { ok: false, error: `Duplicate view key "${key}".` };
    }
    viewKeys.add(key);

    const viewBuilt = buildViewConfigFromDraft({
      viewDraft,
      label: `View "${key}"`,
    });
    if (!viewBuilt.ok) {
      return { ok: false, error: viewBuilt.error };
    }

    views.push({ key, ...viewBuilt.config });
  }

  const data: Record<string, unknown> = {
    schema,
    defaultView: defaultViewBuilt.config,
  };
  const tableName = draft.name.trim();
  if (tableName) data.name = tableName;
  if (draft.isDefault) data.isDefault = true;
  if (views.length > 0) data.views = views;

  const validated = getTablePayload(data);
  if (!validated) {
    return { ok: false, error: "Table payload failed validation." };
  }

  return { ok: true, data };
}

function draftsEqual(a: TableDraft, b: TableDraft): boolean {
  const normalize = (d: TableDraft) => ({
    ...d,
    schema: d.schema.map(({ id: _id, ...rest }) => rest),
  });
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

function newSchemaFieldDraft(): SchemaFieldDraft {
  return {
    id: `field-new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: "",
    key: "",
    type: "string",
    cellEditor: "",
    colWidth: "",
    optionsText: "",
    autofillCurrentDate: false,
    formSpan: 2,
  };
}

function newViewDraft(): ViewDraft {
  return {
    key: "",
    ...newViewConfigDraft({ name: "" }),
  };
}

function newViewConfigDraft({ name }: { name: string }): ViewConfigDraft {
  return {
    name,
    columns: [],
    editFields: [],
    filtersJson: "",
    sortsJson: "",
    extractRegex: "",
  };
}

function SchemaFieldEditor({
  field,
  index,
  total,
  isKeyLocked,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  field: SchemaFieldDraft;
  index: number;
  total: number;
  isKeyLocked: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onChange: (next: SchemaFieldDraft) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card
      className={cn(
        "border-border/80 transition-all duration-150",
        isDragging && "border-primary opacity-40 border-dashed",
        isDragOver && "border-primary bg-primary/5 ring-primary/60 ring-2",
      )}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            className="text-muted-foreground hover:text-foreground -ml-1 flex shrink-0 cursor-grab select-none items-center justify-center rounded p-1 transition-colors hover:bg-muted active:cursor-grabbing"
            title="Drag to reorder"
            aria-label="Drag to reorder"
          >
            <GripVertical className="size-4" />
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-muted-foreground shrink-0 font-mono text-xs">
              #{index + 1}
            </span>
            <span
              className={cn(
                "max-w-[160px] truncate text-sm font-medium sm:max-w-xs",
                !field.name.trim() && "text-muted-foreground italic",
              )}
            >
              {field.name.trim() || "Untitled field"}
            </span>
            {field.key.trim() ? (
              <span className="bg-muted/80 text-muted-foreground border-border/50 hidden truncate rounded border px-1.5 py-0.5 font-mono text-xs sm:inline-block">
                {field.key.trim()}
              </span>
            ) : null}
            <span className="bg-secondary text-secondary-foreground shrink-0 rounded-full px-2 py-0.5 text-xs font-medium">
              {field.type}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <div className="flex items-center" role="group" aria-label="Form width">
            <Button
              type="button"
              variant={field.formSpan === 2 ? "secondary" : "outline"}
              size="sm"
              className="rounded-r-none"
              onClick={() => onChange({ ...field, formSpan: 2 })}
              title="Full line"
              aria-pressed={field.formSpan === 2}
            >
              Full line
            </Button>
            <Button
              type="button"
              variant={field.formSpan === 1 ? "secondary" : "outline"}
              size="sm"
              className="rounded-l-none border-l-0"
              onClick={() => onChange({ ...field, formSpan: 1 })}
              title="Half width"
              aria-pressed={field.formSpan === 1}
            >
              Half
            </Button>
          </div>
          <Button
            type="button"
            variant={isExpanded ? "secondary" : "outline"}
            size="icon-sm"
            onClick={() => setIsExpanded((prev) => !prev)}
            title={isExpanded ? "Collapse properties" : "Edit properties"}
            aria-label={isExpanded ? "Collapse properties" : "Edit properties"}
            aria-expanded={isExpanded}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            disabled={index === 0}
            onClick={onMoveUp}
            title="Move up"
            aria-label="Move up"
          >
            <ArrowUp className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            disabled={index >= total - 1}
            onClick={onMoveDown}
            title="Move down"
            aria-label="Move down"
          >
            <ArrowDown className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onRemove}
            title="Remove field"
            aria-label="Remove field"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="border-t grid gap-4 p-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`field-name-${index}`}>Name</Label>
            <Input
              id={`field-name-${index}`}
              value={field.name}
              onChange={(e) => {
                const name = e.target.value;
                if (isKeyLocked) {
                  onChange({ ...field, name });
                  return;
                }
                onChange({ ...field, name, key: nameToFieldKey(name) });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`field-key-${index}`}>Key</Label>
            <Input
              id={`field-key-${index}`}
              value={field.key}
              readOnly
              disabled
              className="bg-muted/50 font-mono text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`field-type-${index}`}>Type</Label>
            <Select
              value={field.type}
              onValueChange={(type) =>
                onChange({
                  ...field,
                  type,
                  autofillCurrentDate:
                    type === "date" ? field.autofillCurrentDate : false,
                })
              }
            >
              <SelectTrigger id={`field-type-${index}`} className="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {SCHEMA_TYPE_OPTIONS.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`field-colWidth-${index}`}>Column width</Label>
            <Input
              id={`field-colWidth-${index}`}
              type="number"
              value={field.colWidth}
              onChange={(e) => onChange({ ...field, colWidth: e.target.value })}
              placeholder="Optional"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={`field-cellEditor-${index}`}>Cell editor</Label>
            <Input
              id={`field-cellEditor-${index}`}
              value={field.cellEditor}
              onChange={(e) => onChange({ ...field, cellEditor: e.target.value })}
              placeholder='Optional, e.g. "largeText"'
            />
          </div>
          {field.type === "date" && (
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                id={`field-autofillCurrentDate-${index}`}
                type="checkbox"
                checked={field.autofillCurrentDate}
                onChange={(e) =>
                  onChange({ ...field, autofillCurrentDate: e.target.checked })
                }
              />
              Autofill with current date
            </label>
          )}
          {field.type === "select" && (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor={`field-options-${index}`}>Options (one per line)</Label>
              <Textarea
                id={`field-options-${index}`}
                value={field.optionsText}
                onChange={(e) => onChange({ ...field, optionsText: e.target.value })}
                rows={3}
              />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function SchemaKeyOrderedEditor({
  label,
  fields,
  selected,
  disabled,
  onChange,
}: {
  label: string;
  fields: { key: string; name: string }[];
  selected: string[];
  disabled: boolean;
  onChange: (keys: string[]) => void;
}) {
  const fieldByKey = useMemo(
    () => new Map(fields.map((field) => [field.key, field])),
    [fields],
  );

  const unselectedFields = useMemo(
    () => fields.filter((field) => !selected.includes(field.key)),
    [fields, selected],
  );

  const moveSelected = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next);
  };

  const removeSelected = (key: string) => {
    onChange(selected.filter((k) => k !== key));
  };

  const addSelected = (key: string) => {
    if (selected.includes(key)) return;
    onChange([...selected, key]);
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">Add schema fields first.</p>
      ) : (
        <>
          {selected.length === 0 ? (
            <p className="text-muted-foreground text-sm">None selected.</p>
          ) : (
            <ul className="space-y-2">
              {selected.map((key, index) => {
                const field = fieldByKey.get(key);
                const labelText = field?.name.trim() || field?.key || key;
                const isMissing = !field;

                return (
                  <li
                    key={key}
                    className="border-border/80 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
                  >
                    <span
                      className={`text-sm ${isMissing ? "text-muted-foreground italic" : ""}`}
                    >
                      {labelText}
                      {isMissing ? " (removed from schema)" : ""}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled || index === 0}
                        onClick={() => moveSelected(index, -1)}
                      >
                        Up
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled || index >= selected.length - 1}
                        onClick={() => moveSelected(index, 1)}
                      >
                        Down
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        disabled={disabled}
                        onClick={() => removeSelected(key)}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {unselectedFields.length > 0 && (
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs">Add fields</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {unselectedFields.map((field) => (
                  <label
                    key={field.key}
                    className={`flex items-center gap-2 text-sm ${disabled ? "opacity-50" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={false}
                      disabled={disabled}
                      onChange={() => addSelected(field.key)}
                    />
                    {field.name.trim() || field.key}
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ViewConfigEditor({
  idPrefix,
  view,
  schemaFields,
  onChange,
}: {
  idPrefix: string;
  view: ViewConfigDraft;
  schemaFields: { key: string; name: string }[];
  onChange: (next: ViewConfigDraft) => void;
}) {
  const schemaDisabled = schemaFields.length === 0;

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`}>Name</Label>
        <Input
          id={`${idPrefix}-name`}
          value={view.name}
          onChange={(e) => onChange({ ...view, name: e.target.value })}
        />
      </div>
      <SchemaKeyOrderedEditor
        label="Columns"
        fields={schemaFields}
        selected={view.columns}
        disabled={schemaDisabled}
        onChange={(columns) => onChange({ ...view, columns })}
      />
      <SchemaKeyOrderedEditor
        label="Edit fields"
        fields={schemaFields}
        selected={view.editFields}
        disabled={schemaDisabled}
        onChange={(editFields) => onChange({ ...view, editFields })}
      />
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-filters`}>Filters JSON</Label>
        <Textarea
          id={`${idPrefix}-filters`}
          value={view.filtersJson}
          onChange={(e) => onChange({ ...view, filtersJson: e.target.value })}
          rows={4}
          className="font-mono text-xs"
          placeholder="{}"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-sorts`}>Sorts JSON</Label>
        <Textarea
          id={`${idPrefix}-sorts`}
          value={view.sortsJson}
          onChange={(e) => onChange({ ...view, sortsJson: e.target.value })}
          rows={4}
          className="font-mono text-xs"
          placeholder="{}"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-extract-regex`}>Extract regex</Label>
        <Textarea
          id={`${idPrefix}-extract-regex`}
          value={view.extractRegex}
          onChange={(e) => onChange({ ...view, extractRegex: e.target.value })}
          rows={3}
          className="font-mono text-xs"
          placeholder="(?<fieldKey>.*)\n(?<otherKey>.*)"
        />
      </div>
    </>
  );
}

function DefaultViewEditor({
  view,
  schemaFields,
  onChange,
}: {
  view: ViewConfigDraft;
  schemaFields: { key: string; name: string }[];
  onChange: (next: ViewConfigDraft) => void;
}) {
  return (
    <Card className="border-border/80">
      <CardHeader className="border-b px-4 py-3">
        <CardTitle className="text-sm font-medium">Default view</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <ViewConfigEditor
          idPrefix="default-view"
          view={view}
          schemaFields={schemaFields}
          onChange={onChange}
        />
      </CardContent>
    </Card>
  );
}

function ViewEditor({
  view,
  index,
  schemaFields,
  onChange,
  onRemove,
}: {
  view: ViewDraft;
  index: number;
  schemaFields: { key: string; name: string }[];
  onChange: (next: ViewDraft) => void;
  onRemove: () => void;
}) {
  return (
    <Card className="border-border/80">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 border-b px-4 py-3">
        <CardTitle className="text-sm font-medium">View {index + 1}</CardTitle>
        <Button type="button" variant="destructive" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <div className="space-y-2">
          <Label htmlFor={`view-key-${index}`}>Key</Label>
          <Input
            id={`view-key-${index}`}
            value={view.key}
            onChange={(e) => onChange({ ...view, key: e.target.value })}
          />
        </div>
        <ViewConfigEditor
          idPrefix={`view-${index}`}
          view={view}
          schemaFields={schemaFields}
          onChange={(next) => onChange({ ...view, ...next })}
        />
      </CardContent>
    </Card>
  );
}

export function TableEditRoute() {
  const { id } = useParams<{ id: string }>();
  const { blocks, isLoading, updateDecryptedBlock } = useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();

  const tableBlock = useMemo(() => {
    if (!id) return undefined;
    return blocks.find((b) => b.id === id && b.type === "table");
  }, [blocks, id]);

  const persistedDraft = useMemo((): TableDraft | null => {
    if (!tableBlock) return null;
    const payload = getTablePayload(tableBlock.data);
    if (!payload) return null;
    return payloadToDraft(payload);
  }, [tableBlock]);

  const [draft, setDraft] = useState<TableDraft | null>(null);
  const [activeTab, setActiveTab] = useState<TableEditTab>("general");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (persistedDraft) {
      setDraft(persistedDraft);
      setSaveError(null);
    }
  }, [persistedDraft]);

  const isDirty = useMemo(() => {
    if (!draft || !persistedDraft) return false;
    return !draftsEqual(draft, persistedDraft);
  }, [draft, persistedDraft]);

  const schemaFields = useMemo(
    () =>
      (draft?.schema ?? [])
        .map((f) => ({ key: f.key.trim(), name: f.name.trim() }))
        .filter((f) => f.key),
    [draft?.schema],
  );

  const lockedFieldKeys = useMemo(() => {
    if (!persistedDraft) return new Set<string>();
    return new Set(
      persistedDraft.schema.map((field) => field.key.trim()).filter(Boolean),
    );
  }, [persistedDraft]);

  const nameCrumb = isLoading
    ? "…"
    : draft?.name !== undefined
      ? draft.name || "Table"
      : "Table";

  useEffect(() => {
    if (!id) {
      setBreadcrumbSegments([]);
      return () => setBreadcrumbSegments([]);
    }
    setBreadcrumbSegments([
      { label: "Tables", to: "/tables" },
      { label: nameCrumb, to: `/tables/${id}` },
      { label: "Edit" },
    ]);
    return () => setBreadcrumbSegments([]);
  }, [id, nameCrumb, setBreadcrumbSegments]);

  const persistBlockEncrypted = useCallback(
    async ({ block }: { block: DecryptedBlock }): Promise<boolean> => {
      const secretKey =
        typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
      if (!secretKey) {
        console.warn("TableEditRoute: no accessKey in localStorage; cannot persist");
        return false;
      }
      const encryptedData = await encrypt(JSON.stringify(block.data), secretKey);
      const res = await fetch(`/api/blocks/${encodeURIComponent(block.id)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedData }),
      });
      if (!res.ok) {
        console.error("TableEditRoute: persist failed", await res.text());
        return false;
      }
      return true;
    },
    [],
  );

  const handleReset = useCallback(() => {
    if (persistedDraft) {
      setDraft(persistedDraft);
      setSaveError(null);
    }
  }, [persistedDraft]);

  const handleSave = useCallback(async () => {
    if (!tableBlock || !draft) return;
    setSaveError(null);

    const built = buildPayloadFromDraft(draft);
    if (!built.ok) {
      setSaveError(built.error);
      return;
    }

    const nextBlock: DecryptedBlock = {
      id: tableBlock.id,
      type: "table",
      data: built.data,
    };

    setIsSaving(true);
    try {
      const ok = await persistBlockEncrypted({ block: nextBlock });
      if (!ok) {
        setSaveError("Save failed.");
        return;
      }
      updateDecryptedBlock({ block: nextBlock });
    } finally {
      setIsSaving(false);
    }
  }, [draft, persistBlockEncrypted, tableBlock, updateDecryptedBlock]);

  const updateSchemaField = useCallback(
    (index: number, next: SchemaFieldDraft) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const schema = [...prev.schema];
        schema[index] = next;
        return { ...prev, schema };
      });
    },
    [],
  );

  const removeSchemaField = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, schema: prev.schema.filter((_, i) => i !== index) };
    });
  }, []);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const reorderSchemaField = useCallback((fromIndex: number, toIndex: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.schema.length ||
        toIndex >= prev.schema.length
      ) {
        return prev;
      }
      const schema = [...prev.schema];
      const [item] = schema.splice(fromIndex, 1);
      schema.splice(toIndex, 0, item);
      return { ...prev, schema };
    });
  }, []);

  const moveSchemaField = useCallback(
    (index: number, direction: -1 | 1) => {
      reorderSchemaField(index, index + direction);
    },
    [reorderSchemaField],
  );

  const handleDragStart = useCallback((index: number, e: React.DragEvent) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  }, []);

  const handleDragOver = useCallback((index: number, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex((prev) => (prev !== index ? index : prev));
  }, []);

  const handleDragLeave = useCallback((index: number, e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDragOverIndex((prev) => (prev === index ? null : prev));
    }
  }, []);

  const handleDrop = useCallback(
    (targetIndex: number, e: React.DragEvent) => {
      e.preventDefault();
      const sourceIndexRaw = e.dataTransfer.getData("text/plain");
      const parsedSourceIndex = Number.parseInt(sourceIndexRaw, 10);
      const sourceIndex =
        draggedIndex ?? (Number.isNaN(parsedSourceIndex) ? -1 : parsedSourceIndex);

      setDraggedIndex(null);
      setDragOverIndex(null);

      if (sourceIndex >= 0 && sourceIndex !== targetIndex) {
        reorderSchemaField(sourceIndex, targetIndex);
      }
    },
    [draggedIndex, reorderSchemaField],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const updateDefaultView = useCallback((next: ViewConfigDraft) => {
    setDraft((prev) => (prev ? { ...prev, defaultView: next } : prev));
  }, []);

  const updateView = useCallback((index: number, next: ViewDraft) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const views = [...prev.views];
      views[index] = next;
      return { ...prev, views };
    });
  }, []);

  const removeView = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, views: prev.views.filter((_, i) => i !== index) };
    });
  }, []);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-6 p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-base font-semibold leading-none tracking-tight">
          Edit table
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={
              !isDirty
                ? "text-muted-foreground text-xs"
                : "text-xs text-amber-600 dark:text-amber-500"
            }
          >
            {!isDirty ? "Saved" : "Unsaved changes"}
          </span>
          <Button
            type="button"
            variant="outline"
            disabled={!isDirty || isSaving}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            type="button"
            disabled={!draft || !isDirty || isSaving}
            className="gap-2"
            onClick={() => void handleSave()}
          >
            {isSaving ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      {saveError && (
        <p className="text-destructive text-sm" role="alert">
          {saveError}
        </p>
      )}

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : !tableBlock || !draft ? (
        <p className="text-muted-foreground text-sm">Table not found.</p>
      ) : (
        <div className="space-y-4">
          <div
            role="tablist"
            aria-label="Table settings sections"
            className="border-border flex flex-wrap gap-2 border-b pb-3"
          >
            {TABLE_EDIT_TABS.map((tab) => (
              <Button
                key={tab.id}
                type="button"
                size="sm"
                role="tab"
                aria-selected={activeTab === tab.id}
                variant={activeTab === tab.id ? "default" : "secondary"}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {activeTab === "general" && (
            <Card className="gap-0">
              <CardContent className="space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="table-name">Name</Label>
                  <Input
                    id="table-name"
                    value={draft.name}
                    onChange={(e) =>
                      setDraft((prev) =>
                        prev ? { ...prev, name: e.target.value } : prev,
                      )
                    }
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.isDefault}
                    onChange={(e) =>
                      setDraft((prev) =>
                        prev ? { ...prev, isDefault: e.target.checked } : prev,
                      )
                    }
                  />
                  Default table
                </label>
              </CardContent>
            </Card>
          )}

          {activeTab === "schema" && (
            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDraft((prev) =>
                      prev
                        ? { ...prev, schema: [...prev.schema, newSchemaFieldDraft()] }
                        : prev,
                    )
                  }
                >
                  Add field
                </Button>
              </div>
              {draft.schema.length === 0 ? (
                <p className="text-muted-foreground text-sm">No schema fields yet.</p>
              ) : (
                <div className="space-y-4">
                  {packFormRows({ fields: draft.schema }).map((row) => (
                    <div
                      key={row.map((field) => field.id).join("-")}
                      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
                    >
                      {row.map((field) => {
                        const index = draft.schema.findIndex(
                          (item) => item.id === field.id,
                        );
                        return (
                          <div
                            key={field.id}
                            className={cn(field.formSpan !== 1 && "sm:col-span-2")}
                          >
                            <SchemaFieldEditor
                              field={field}
                              index={index}
                              total={draft.schema.length}
                              isKeyLocked={lockedFieldKeys.has(field.key.trim())}
                              isDragging={draggedIndex === index}
                              isDragOver={
                                dragOverIndex === index && draggedIndex !== index
                              }
                              onDragStart={(e) => handleDragStart(index, e)}
                              onDragOver={(e) => handleDragOver(index, e)}
                              onDragLeave={(e) => handleDragLeave(index, e)}
                              onDrop={(e) => handleDrop(index, e)}
                              onDragEnd={handleDragEnd}
                              onChange={(next) => updateSchemaField(index, next)}
                              onRemove={() => removeSchemaField(index)}
                              onMoveUp={() => moveSchemaField(index, -1)}
                              onMoveDown={() => moveSchemaField(index, 1)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {activeTab === "views" && (
            <section className="space-y-4">
              <DefaultViewEditor
                view={draft.defaultView}
                schemaFields={schemaFields}
                onChange={updateDefaultView}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-medium">Additional views</h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDraft((prev) =>
                      prev ? { ...prev, views: [...prev.views, newViewDraft()] } : prev,
                    )
                  }
                >
                  Add view
                </Button>
              </div>
              {draft.views.length === 0 ? (
                <p className="text-muted-foreground text-sm">No additional views yet.</p>
              ) : (
                draft.views.map((view, index) => (
                  <ViewEditor
                    key={index}
                    view={view}
                    index={index}
                    schemaFields={schemaFields}
                    onChange={(next) => updateView(index, next)}
                    onRemove={() => removeView(index)}
                  />
                ))
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
